import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { Plus, Edit3, Trash2, Save, Search, XCircle, ChevronDown } from 'lucide-react';
import { useStandardQA, useStandardQADetail, useStandardQAMutations } from '../../../hooks/console/useConsoleAPI';
import Pagination from '../shared/Pagination';
import { CONSOLE_CATEGORIES } from '../../../constants/consoleConstants';

const PER = 10;

const EMPTY_FORM = {
    qa_code: '', category: 'definition',
    question: '', standard_answer: '',
    reference_law_text: '',
    expected_question_type: 'standard',
    expected_core_article: '', expected_detail_article: '',
    expected_sanction_article: '',
    expected_targets_json: '',
    must_include_terms_json: '',
    must_not_include_terms_json: '',
    score_pass: 80, score_warn: 60,
    is_active: 'Y',
};

export default function StandardQATab() {
    const [params, setParams] = useState({ page: 1, size: PER });
    const [keyword, setKeyword] = useState('');
    const [filterCat, setFilterCat] = useState('');
    const [filterActive, setFilterActive] = useState('');
    const [selectedId, setSelectedId] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);

    const { data, isLoading } = useStandardQA(params);
    const { data: selectedDetail, isLoading: detailLoading } = useStandardQADetail(selectedId);
    const { data: editDetail } = useStandardQADetail(editingId);
    const { create, update, remove } = useStandardQAMutations();

    const items = data?.items ?? [];
    const total = data?.total ?? 0;
    const totalPages = Math.ceil(total / PER);

    const catLabel = (key) => CONSOLE_CATEGORIES.find((c) => c.key === key)?.label ?? key;

    const applySearch = () => {
        setParams((p) => ({
            ...p, page: 1,
            keyword: keyword || undefined,
            category: filterCat || undefined,
            is_active: filterActive || undefined,
        }));
    };

    const openNew = () => {
        const ts = Date.now().toString().slice(-6);
        setEditingId(null);
        setEditItem({ ...EMPTY_FORM, qa_code: `SQ_${ts}` });
        setIsEdit(false);
        setShowAdvanced(false);
        setShowForm(true);
    };

    const openEdit = (item) => {
        setEditingId(item.id);
        setIsEdit(true);
        setShowForm(true);
    };

    // 편집 상세 데이터 로드 시 폼 반영
    useEffect(() => {
        if (editDetail && isEdit) {
            setEditItem({
                ...editDetail,
                expected_targets_json: (editDetail.expected_targets_json ?? []).join(', '),
                must_include_terms_json: (editDetail.must_include_terms_json ?? []).join(', '),
                must_not_include_terms_json: (editDetail.must_not_include_terms_json ?? []).join(', '),
            });
        }
    }, [editDetail]);

    const parseForm = (form) => ({
        ...form,
        expected_targets_json: form.expected_targets_json
            ? form.expected_targets_json.split(',').map((s) => s.trim()).filter(Boolean)
            : [],
        must_include_terms_json: form.must_include_terms_json
            ? form.must_include_terms_json.split(',').map((s) => s.trim()).filter(Boolean)
            : [],
        must_not_include_terms_json: form.must_not_include_terms_json
            ? form.must_not_include_terms_json.split(',').map((s) => s.trim()).filter(Boolean)
            : [],
        score_pass: Number(form.score_pass),
        score_warn: Number(form.score_warn),
    });

    const handleSave = () => {
        if (!editItem.question || !editItem.standard_answer || !editItem.qa_code) return;
        const payload = parseForm(editItem);
        if (isEdit) {
            update.mutate({ id: editItem.id, data: payload }, { onSuccess: () => setShowForm(false) });
        } else {
            create.mutate(payload, { onSuccess: () => setShowForm(false) });
        }
    };

    const handleDelete = (id) => {
        if (!window.confirm('삭제하시겠습니까?')) return;
        remove.mutate(id);
    };

    const isSaving = create.isLoading || update.isLoading;

    return (
        <div className="space-y-4 w-full">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                    <p className="text-lg font-bold text-sb-header">표준 질문/답안 관리</p>
                    <p className="text-base text-gray-400">AI가 맞춰야 할 질문과 정답을 등록합니다. 등록된 질문으로 AI를 테스트할 수 있습니다.</p>
                </div>
                <button onClick={openNew} className="flex items-center gap-1.5 px-3 py-2 bg-sb-primary hover:bg-blue-700 text-white text-base font-medium rounded-lg transition-colors shrink-0">
                    <Plus size={14} /> 새 질문 등록
                </button>
            </div>

            {/* 검색/필터 */}
            <div className="rounded-xl border border-gray-100 bg-white p-3 shadow-sm flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && applySearch()}
                        placeholder="질문/답안 검색..."
                        className="w-full pl-9 pr-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                    />
                </div>
                <select
                    value={filterCat}
                    onChange={(e) => setFilterCat(e.target.value)}
                    className="px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                >
                    <option value="">전체 카테고리</option>
                    {CONSOLE_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
                <select
                    value={filterActive}
                    onChange={(e) => setFilterActive(e.target.value)}
                    className="px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                >
                    <option value="">전체 상태</option>
                    <option value="Y">활성</option>
                    <option value="N">비활성</option>
                </select>
                <button onClick={applySearch} className="px-4 py-2 rounded-lg text-base font-medium bg-sb-primary text-white hover:bg-blue-700 transition-colors shrink-0">
                    검색
                </button>
                <span className="text-xs text-gray-500 flex items-center px-2">총 {total}건</span>
            </div>

            {/* 목록 */}
            <div className="space-y-2">
                {isLoading && <p className="text-center py-8 text-base text-gray-400">불러오는 중...</p>}
                {!isLoading && items.map((item) => (
                    <div
                        key={item.id}
                        className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm hover:shadow-md hover:border-gray-300 transition-all cursor-pointer"
                        onClick={() => setSelectedId(selectedId === item.id ? null : item.id)}
                    >
                        <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1.5">
                                    <span className="text-sm px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700">{catLabel(item.category)}</span>
                                    <span className="text-sm font-mono text-gray-400">{item.qa_code}</span>
                                    <span className={`text-sm px-1.5 py-0.5 rounded-full font-medium ${item.is_active === 'Y' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-500'}`}>
                                        {item.is_active === 'Y' ? '활성' : '비활성'}
                                    </span>
                                </div>
                                <p className="text-base font-medium text-sb-header mb-1">{item.question}</p>
                                {selectedId !== item.id && <p className="text-base text-gray-500 line-clamp-2">{item.standard_answer}</p>}
                            </div>
                            <div className="flex items-center gap-1 ml-3" onClick={(e) => e.stopPropagation()}>
                                <button onClick={() => openEdit(item)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                                    <Edit3 size={13} className="text-gray-400" />
                                </button>
                                <button onClick={() => handleDelete(item.id)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors">
                                    <Trash2 size={13} className="text-red-400" />
                                </button>
                                <ChevronDown size={13} className={`text-gray-400 transition-transform ${selectedId === item.id ? 'rotate-180' : ''}`} />
                            </div>
                        </div>

                        {selectedId === item.id && (
                            <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                                {detailLoading && <p className="text-base text-gray-400 text-center py-2">불러오는 중...</p>}
                                {selectedDetail && (
                                    <>
                                        <div>
                                            <p className="text-sm uppercase tracking-wider text-gray-400 mb-1">표준 답안</p>
                                            <p className="text-base text-gray-700 leading-relaxed">{selectedDetail.standard_answer}</p>
                                        </div>
                                        {selectedDetail.reference_law_text && (
                                            <div>
                                                <p className="text-sm uppercase tracking-wider text-gray-400 mb-1">관련 법령</p>
                                                <p className="text-base text-gray-700">{selectedDetail.reference_law_text}</p>
                                            </div>
                                        )}
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <p className="text-sm uppercase tracking-wider text-gray-400 mb-1">핵심 조문</p>
                                                <div className="flex flex-wrap gap-1">
                                                    {[selectedDetail.expected_core_article, selectedDetail.expected_detail_article, selectedDetail.expected_sanction_article].filter(Boolean).map((a, i) => (
                                                        <span key={i} className="text-sm px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">{a}</span>
                                                    ))}
                                                </div>
                                            </div>
                                            <div>
                                                <p className="text-sm uppercase tracking-wider text-gray-400 mb-1">PASS / WARN 기준</p>
                                                <p className="text-base text-gray-600">PASS ≥ {selectedDetail.score_pass}점 / WARN ≥ {selectedDetail.score_warn}점</p>
                                            </div>
                                        </div>
                                        {(selectedDetail.must_include_terms_json ?? []).length > 0 && (
                                            <div>
                                                <p className="text-sm uppercase tracking-wider text-gray-400 mb-1">필수 포함 키워드</p>
                                                <div className="flex flex-wrap gap-1">
                                                    {(selectedDetail.must_include_terms_json ?? []).map((v, i) => (
                                                        <span key={i} className="text-sm px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{v}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {(selectedDetail.must_not_include_terms_json ?? []).length > 0 && (
                                            <div>
                                                <p className="text-sm uppercase tracking-wider text-gray-400 mb-1">금지 키워드</p>
                                                <div className="flex flex-wrap gap-1">
                                                    {(selectedDetail.must_not_include_terms_json ?? []).map((v, i) => (
                                                        <span key={i} className="text-sm px-1.5 py-0.5 rounded bg-red-100 text-red-600 flex items-center gap-1">
                                                            <XCircle size={9} /> {v}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                ))}
                {!isLoading && items.length === 0 && (
                    <div className="text-center py-12 space-y-2">
                        {(params.keyword || params.category || params.is_active) ? (
                            <p className="text-base text-gray-400">검색 조건에 맞는 QA가 없습니다.</p>
                        ) : (
                            <>
                                <p className="text-base font-medium text-gray-500">아직 등록된 표준 QA가 없습니다.</p>
                                <p className="text-sm text-gray-400">위의 <strong>새 표준 QA 등록</strong> 버튼을 눌러 첫 번째 기준 질문을 추가하세요.</p>
                                <p className="text-sm text-gray-400">예: <em>"밀폐공간 작업 시 산소농도 기준은?"</em> → 모범 답안과 함께 등록</p>
                            </>
                        )}
                    </div>
                )}
            </div>

            <Pagination currentPage={params.page} totalPages={totalPages} onPageChange={(p) => setParams((prev) => ({ ...prev, page: p }))} />

            {/* 등록/수정 모달 */}
            <Modal show={showForm} centered size="lg" onHide={() => setShowForm(false)} contentClassName="rounded-2xl border-0 shadow-xl">
                <Modal.Header className="border-b border-gray-100 px-6 py-3">
                    <Modal.Title className="text-base font-bold text-sb-header">
                        {isEdit ? '표준 QA 수정' : '새 표준 QA 등록'}
                    </Modal.Title>
                    <button type="button" className="modal-btn-close" onClick={() => setShowForm(false)}>
                        <span className="modal-btn-span">X</span>
                    </button>
                </Modal.Header>
                <Modal.Body className="max-h-[70vh] overflow-y-auto px-6 py-4 space-y-4">
                    {editItem && (
                        <>
                            {/* 필수 입력 섹션 */}
                            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 space-y-3">
                                <p className="text-sm font-semibold text-blue-700">필수 입력</p>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">
                                        QA 코드 {isEdit ? '*' : <span className="text-xs text-blue-500 normal-case font-normal ml-1">(자동 생성)</span>}
                                    </label>
                                    <input
                                        value={editItem.qa_code}
                                        onChange={(e) => setEditItem({ ...editItem, qa_code: e.target.value })}
                                        readOnly={!isEdit}
                                        className={`w-full px-3 py-2 rounded-lg text-base border font-mono outline-none focus:border-sb-primary ${isEdit ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 text-gray-500'}`}
                                    />
                                </div>
                                <div>
                                    <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">카테고리</label>
                                    <select
                                        value={editItem.category}
                                        onChange={(e) => setEditItem({ ...editItem, category: e.target.value })}
                                        className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                                    >
                                        {CONSOLE_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">질문 *</label>
                                <input
                                    value={editItem.question}
                                    onChange={(e) => setEditItem({ ...editItem, question: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                                    placeholder="표준 질문 입력"
                                />
                            </div>
                            <div>
                                <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">표준 답안 *</label>
                                <textarea
                                    value={editItem.standard_answer}
                                    onChange={(e) => setEditItem({ ...editItem, standard_answer: e.target.value })}
                                    rows={4}
                                    className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary resize-y"
                                    placeholder="표준 답안 입력"
                                />
                            </div>
                            </div>{/* end 필수 입력 섹션 */}

                            {/* 선택 입력 섹션 */}
                            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3">
                                <div>
                                    <p className="text-sm font-semibold text-gray-600">선택 입력</p>
                                    <p className="text-xs text-gray-400 mt-0.5">입력할수록 AI 채점 정확도가 높아집니다. 처음엔 비워둬도 됩니다.</p>
                                </div>
                            <div>
                                <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">관련 법령 텍스트</label>
                                <input
                                    value={editItem.reference_law_text ?? ''}
                                    onChange={(e) => setEditItem({ ...editItem, reference_law_text: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                                    placeholder="예: 산업안전보건법 제29조, 시행령 별표4"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">질문 유형</label>
                                    <select
                                        value={editItem.expected_question_type ?? 'standard'}
                                        onChange={(e) => setEditItem({ ...editItem, expected_question_type: e.target.value })}
                                        className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                                    >
                                        <option value="standard">일반</option>
                                        <option value="education">교육</option>
                                        <option value="punishment">벌칙</option>
                                        <option value="definition">정의</option>
                                        <option value="procedure">절차</option>
                                        <option value="numeric">수치</option>
                                        <option value="applicability">적용 여부</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">핵심 법령 조항</label>
                                    <input
                                        value={editItem.expected_core_article ?? ''}
                                        onChange={(e) => setEditItem({ ...editItem, expected_core_article: e.target.value })}
                                        className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                                        placeholder="예: 제29조"
                                    />
                                    <p className="text-xs text-gray-400 mt-1">AI가 반드시 참조해야 할 주요 조항</p>
                                </div>
                                <div>
                                    <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">세부 시행 조항</label>
                                    <input
                                        value={editItem.expected_detail_article ?? ''}
                                        onChange={(e) => setEditItem({ ...editItem, expected_detail_article: e.target.value })}
                                        className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                                        placeholder="예: 시행령 별표4"
                                    />
                                    <p className="text-xs text-gray-400 mt-1">시행령·시행규칙 등 세부 규정 조항</p>
                                </div>
                                <div>
                                    <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">제재·벌칙 조항</label>
                                    <input
                                        value={editItem.expected_sanction_article ?? ''}
                                        onChange={(e) => setEditItem({ ...editItem, expected_sanction_article: e.target.value })}
                                        className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                                        placeholder="예: 제167조"
                                    />
                                    <p className="text-xs text-gray-400 mt-1">위반 시 처벌·과태료 관련 조항</p>
                                </div>
                            </div>
                            <div>
                                <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">적용 대상 (쉼표 구분)</label>
                                <input
                                    value={editItem.expected_targets_json ?? ''}
                                    onChange={(e) => setEditItem({ ...editItem, expected_targets_json: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                                    placeholder="예: 사업주, 근로자"
                                />
                            </div>
                            <div>
                                <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">필수 포함 키워드 (쉼표 구분)</label>
                                <input
                                    value={editItem.must_include_terms_json ?? ''}
                                    onChange={(e) => setEditItem({ ...editItem, must_include_terms_json: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                                    placeholder="예: 8시간, 신규채용, 일용근로자"
                                />
                            </div>
                            <div>
                                <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">금지 키워드 (쉼표 구분)</label>
                                <input
                                    value={editItem.must_not_include_terms_json ?? ''}
                                    onChange={(e) => setEditItem({ ...editItem, must_not_include_terms_json: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                                    placeholder="예: 6시간, 적용되지 않습니다"
                                />
                            </div>
                            <div>
                                <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">사용 여부</label>
                                <select
                                    value={editItem.is_active}
                                    onChange={(e) => setEditItem({ ...editItem, is_active: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                                >
                                    <option value="Y">사용 중 (평가에 포함)</option>
                                    <option value="N">사용 안 함 (평가에서 제외)</option>
                                </select>
                            </div>

                            {/* 고급 설정 토글 */}
                            <button
                                type="button"
                                onClick={() => setShowAdvanced((v) => !v)}
                                className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <ChevronDown size={13} className={`transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
                                {showAdvanced ? '고급 설정 접기' : '고급 설정 (점수 기준 변경)'}
                            </button>
                            {showAdvanced && (
                                <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 space-y-3">
                                    <p className="text-xs text-gray-400">기본값(정상 80점 / 주의 60점)을 그대로 두는 것을 권장합니다. 변경 시 이 질문에만 적용됩니다.</p>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="text-sm text-gray-500 block mb-1">정상 판정 기준 점수</label>
                                            <input
                                                type="number" min={0} max={100}
                                                value={editItem.score_pass}
                                                onChange={(e) => setEditItem({ ...editItem, score_pass: e.target.value })}
                                                className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                                            />
                                            <p className="text-xs text-gray-400 mt-1">이 점수 이상이면 AI 정답으로 인정</p>
                                        </div>
                                        <div>
                                            <label className="text-sm text-gray-500 block mb-1">주의 판정 기준 점수</label>
                                            <input
                                                type="number" min={0} max={100}
                                                value={editItem.score_warn}
                                                onChange={(e) => setEditItem({ ...editItem, score_warn: e.target.value })}
                                                className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                                            />
                                            <p className="text-xs text-gray-400 mt-1">이 점수 이상이면 검토 권고</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            </div>{/* end 선택 입력 섹션 */}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 px-6 pb-4 pt-0">
                    <Button variant="light" className="text-gray-600" onClick={() => setShowForm(false)}>취소</Button>
                    <Button variant="primary" className="bg-sb-primary border-0" onClick={handleSave} disabled={isSaving}>
                        <Save size={13} className="me-1" />{isSaving ? '저장 중...' : '저장'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
